import type { StudentAssignmentCubit } from '@breakoutlearning/firebase-repository/cubits/StudentAssignmentCubit'
import { Button } from 'components/Button'
import { Shield } from 'components/icons/Shield'
import { useDialogs } from 'hooks/dialogs'
import { observer } from 'mobx-react-lite'
import { useCallback } from 'react'
import classNames from 'classnames'
import { RoomStateForm } from './RoomStateFormDialog'
import { useBreakoutUser } from 'hooks/profile'
import {
  AssignmentType,
  RoomStateVideoMethod,
} from '@breakoutlearning/firebase-repository/types'
import { capitalize } from 'util/strings'
import { BreakoutUserAvatar } from 'components/breakout/BreakoutUserAvatar'
import { RoomStateStatus } from '@breakoutlearning/firebase-repository/models/RoomState'
import { useTranslationTyped } from 'i18n/i18n'
import { AdminOnlyRing } from 'components/AdminOnlyRing'

export const ExistingRoomState = observer(function ExistingRoomState({
  cubit,
  headingId,
}: {
  cubit: StudentAssignmentCubit
  headingId: string
}) {
  const user = useBreakoutUser()

  const { tt } = useTranslationTyped()
  const { showDialog, popDialog } = useDialogs()

  const leaveRoom = useCallback(() => {
    cubit.leaveRoom()
  }, [cubit])

  const libraryObject = cubit.libraryObject

  const userIsInRoom = cubit.roomState.data.userIds.includes(user.uid)

  const canLeave =
    !libraryObject.afterSession &&
    libraryObject.assignment.assignmentType === AssignmentType.ManyGroups &&
    userIsInRoom

  const scheduledAtDate = cubit.roomState.scheduledAtDate
  const noGroupLeader = cubit.roomState.data.groupLeaderUserIds.length === 0

  const canEdit =
    ![
      RoomStateStatus.completed,
      RoomStateStatus.expired,
      RoomStateStatus.abandoned,
      RoomStateStatus.queuedForGrading,
    ].includes(cubit.libraryObject.roomStateStatus) &&
    (cubit.currentUserIsGroupLeader ||
      noGroupLeader ||
      user.isInstructor ||
      user.isTA ||
      user.isCorre)

  return (
    <div className="w-full">
      <h1 id={headingId} className="mb-3 text-headline-large">
        {cubit.roomState.roomStateName}
      </h1>
      <div className="mb-3 flex flex-row justify-between text-xs text-gray-800">
        <div>{tt.student_assignment.meeting_time()}</div>
        {canEdit && (
          <button
            className="cursor-pointer"
            onClick={(e) =>
              showDialog(
                () => (
                  <RoomStateForm
                    onSave={() => {
                      popDialog()
                    }}
                    cubit={cubit}
                  />
                ),
                { trigger: e.currentTarget }
              )
            }
          >
            {tt.student_assignment.edit()}
          </button>
        )}
      </div>
      <div className="flex flex-row justify-between rounded-2xl bg-surface p-5">
        <div className="flex flex-col items-center">
          <div className="mb-2 text-xs">{tt.design_system.date()}</div>
          <div className="font-bold">
            {!scheduledAtDate && tt.student_assignment.not()}
            {scheduledAtDate && scheduledAtDate?.toFormat('LLL d, yyyy')}
          </div>
        </div>
        <div className="flex flex-col items-center">
          <div className="mb-2 text-xs">{tt.design_system.time()}</div>
          <div className="font-bold">
            {!scheduledAtDate && tt.student_assignment.scheduled()}
            {scheduledAtDate && scheduledAtDate?.toFormat('t')}
          </div>
        </div>
      </div>
      <AdminOnlyRing
        ringClassName="mt-3"
        onlyShowIf={user.isCorre}
        label={tt.roles.corre_only()}
      >
        <div className="flex flex-row justify-between">
          <div className="mb-2 text-xs text-gray-800">
            {tt.student_assignment.video_method()}
          </div>
          <button
            // disabled={cubit.roomState.didStart}
            aria-live="polite"
            aria-label={[
              tt.student_assignment.video_method(),
              capitalize(cubit.libraryObject.videoMethod),
            ].join(' ')}
            onClick={() => {
              cubit.updateRoomVideoMethod(
                cubit.libraryObject.videoMethod ===
                  RoomStateVideoMethod.broadcast
                  ? RoomStateVideoMethod.streaming
                  : RoomStateVideoMethod.broadcast
              )
            }}
            className={classNames(
              'mb-2 select-none text-label-medium text-gray-800',
              cubit.currentUserIsGroupLeader && 'cursor-pointer'
            )}
          >
            {capitalize(cubit.libraryObject.videoMethod)}
          </button>
        </div>
      </AdminOnlyRing>
      <div className="mt-3">
        <div className="mb-2 text-xs text-gray-800">
          {tt.instructor_assignment.students()}
        </div>
        <div>
          {cubit.sortedRoomStateUsers.map((student) => {
            const isGroupLeader =
              cubit.roomState.data.groupLeaderUserIds.includes(student.id)
            return (
              <div key={student.id} className="mb-2 flex flex-row items-center">
                <div className="overflow-hidden">
                  <BreakoutUserAvatar radius={13} user={student} />
                </div>
                <div className="ml-2 flex flex-row text-label-medium">
                  {student.data.firstName} {student.data.lastName}
                  {isGroupLeader && (
                    <Shield size={14} className="ml-1 stroke-breakout-gold" />
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
      {canLeave && (
        <div>
          <Button
            testId="room-dialog-leave-room"
            className="mt-5 w-full bg-core-error text-core-on-error"
            onClick={() => leaveRoom()}
          >
            {tt.student_assignment.leave_group()}
          </Button>
        </div>
      )}
    </div>
  )
})
